export default function ComparisonSection() {
  return (
    <section className="relative w-full py-32 bg-[#0a0c0f]">
      <div className="container mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 bg-[#00d4ff]/10 border border-[#00d4ff]/30 rounded-full mb-8">
            <i className="ri-database-2-line text-[#00d4ff] mr-3 text-xl"></i>
            <span className="text-[#00d4ff] font-bold text-sm tracking-wider">DATA SUPERIORITY</span>
          </div>
          <h2 className="text-6xl font-black text-white mb-3" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            Why Spatial Big Data?
          </h2>
          <p className="text-2xl text-[#00d4ff] font-bold mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            なぜ、「空間ビッグデータ」が必要なのか？
          </p>
          <p className="text-xl text-[#b0b8c1] max-w-4xl mx-auto leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
            従来の90度カメラでは捉えられない「現場の全て」を記録し、AIの教師データとして活用
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 relative mb-20">
          {/* Center VS Badge */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 hidden lg:block">
            <div className="w-20 h-20 rounded-full bg-[#0f1115] border-2 border-[#00d4ff] flex items-center justify-center shadow-[0_0_30px_rgba(0,212,255,0.5)]">
              <span className="text-2xl font-black text-[#00d4ff]" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>VS</span>
            </div>
          </div>

          {/* Left Side - Traditional */}
          <div className="relative bg-[#0a0c0f] p-12 lg:p-16 flex flex-col items-center justify-center min-h-[600px] border-r border-[#00d4ff]/30">
            <div className="text-center max-w-md">
              {/* Icon */}
              <div className="mb-12">
                <div className="w-24 h-24 mx-auto flex items-center justify-center">
                  <i className="ri-camera-line text-6xl text-gray-600"></i>
                </div>
              </div>

              {/* Visual */}
              <div className="mb-12 relative">
                <div className="w-64 h-40 mx-auto bg-[#1a1d23] rounded-lg border-2 border-gray-700 overflow-hidden relative">
                  <img
                    src="https://readdy.ai/api/search-image?query=Limited%20narrow%20field%20of%20view%20from%20a%20traditional%2090%20degree%20security%20camera%20showing%20only%20a%20small%20portion%20of%20a%20factory%20floor%2C%20dark%20edges%20representing%20blind%20spots%2C%20restricted%20perspective%2C%20grainy%20surveillance%20footage%20style%2C%20low%20visibility%20industrial%20environment&width=400&height=250&seq=narrow-view-001&orientation=landscape"
                    alt="Narrow View"
                    className="w-full h-full object-cover object-top opacity-50"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-black/80"></div>
                </div>
                {/* Blind Spot Labels */}
                <div className="absolute -left-8 top-1/2 transform -translate-y-1/2">
                  <span className="text-xs text-red-500 font-bold tracking-wider">BLIND SPOT</span>
                </div>
                <div className="absolute -right-8 top-1/2 transform -translate-y-1/2">
                  <span className="text-xs text-red-500 font-bold tracking-wider">BLIND SPOT</span>
                </div>
              </div>

              {/* Text */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  断絶されたデータ
                </h3>
                <p className="text-base text-[#b0b8c1] leading-relaxed mb-6" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  従来型カメラは、文脈の90%を見逃している。<br />
                  死角だらけのデータ。
                </p>
                {/* Stats */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-[#1a1d23] px-4 py-3 rounded-lg">
                    <span className="text-sm text-gray-400">視野角</span>
                    <span className="text-lg font-bold text-white">90°</span>
                  </div>
                  <div className="flex items-center justify-between bg-[#1a1d23] px-4 py-3 rounded-lg">
                    <span className="text-sm text-gray-400">情報量</span>
                    <span className="text-lg font-bold text-white">1x</span>
                  </div>
                  <div className="flex items-center justify-between bg-[#1a1d23] px-4 py-3 rounded-lg">
                    <span className="text-sm text-gray-400">死角</span>
                    <span className="text-lg font-bold text-red-500">90%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Spatial Big Data */}
          <div className="relative bg-[#1a1d23] p-12 lg:p-16 flex flex-col items-center justify-center min-h-[600px]">
            <div className="text-center max-w-md">
              {/* Icon */}
              <div className="mb-12">
                <div className="w-24 h-24 mx-auto flex items-center justify-center">
                  <i className="ri-360-line text-6xl text-[#00d4ff] drop-shadow-[0_0_15px_rgba(0,212,255,0.8)]"></i>
                </div>
              </div>

              {/* Visual - 360 Degree Sphere */}
              <div className="mb-12 relative">
                <div className="w-64 h-64 mx-auto relative">
                  {/* 360 Degree Sphere with Scanning Lines */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="relative w-48 h-48">
                      {/* Outer Glow Ring */}
                      <div className="absolute inset-0 rounded-full bg-[#00d4ff]/20 blur-2xl animate-pulse"></div>
                      
                      {/* Main Sphere */}
                      <div className="absolute inset-0 rounded-full border-4 border-[#00d4ff] bg-gradient-to-br from-[#00d4ff]/30 via-[#0099cc]/20 to-transparent shadow-[0_0_60px_rgba(0,212,255,0.6)] animate-pulse"></div>
                      
                      {/* Horizontal Scanning Lines */}
                      <div className="absolute inset-0 overflow-hidden rounded-full">
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={`h${i}`}
                            className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent"
                            style={{
                              top: `${(i + 1) * 12}%`,
                              opacity: 0.6,
                              animation: `scanHorizontal ${2 + i * 0.2}s ease-in-out infinite`,
                              animationDelay: `${i * 0.1}s`
                            }}
                          ></div>
                        ))}
                      </div>
                      
                      {/* Vertical Scanning Lines */}
                      <div className="absolute inset-0 overflow-hidden rounded-full">
                        {[...Array(8)].map((_, i) => (
                          <div
                            key={`v${i}`}
                            className="absolute h-full w-0.5 bg-gradient-to-b from-transparent via-[#ff6b35] to-transparent"
                            style={{
                              left: `${(i + 1) * 12}%`,
                              opacity: 0.5,
                              animation: `scanVertical ${2.5 + i * 0.2}s ease-in-out infinite`,
                              animationDelay: `${i * 0.15}s`
                            }}
                          ></div>
                        ))}
                      </div>
                      
                      {/* Center Core */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00d4ff] to-[#0099cc] shadow-[0_0_40px_rgba(0,212,255,0.8)] animate-pulse"></div>
                      </div>
                      
                      {/* 360 Degree Markers */}
                      <div className="absolute inset-0">
                        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                          <div
                            key={angle}
                            className="absolute w-2 h-2 bg-[#00d4ff] rounded-full shadow-[0_0_10px_rgba(0,212,255,0.8)]"
                            style={{
                              top: '50%',
                              left: '50%',
                              transform: `rotate(${angle}deg) translateY(-96px) translateX(-4px)`,
                            }}
                          ></div>
                        ))}
                      </div>
                      
                      {/* Rotating Ring */}
                      <div className="absolute inset-[-8px] border-2 border-dashed border-[#00d4ff]/40 rounded-full animate-spin-slow"></div>
                    </div>
                  </div>
                  
                  {/* 360° Label */}
                  <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
                    <div className="px-4 py-2 bg-[#00d4ff] rounded-full">
                      <span className="text-sm font-black text-black">360° COVERAGE</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Text */}
              <div>
                <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  空間ビッグデータ
                </h3>
                <p className="text-base text-white leading-relaxed mb-6" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
                  360度、死角なし。<br />
                  視野の12倍の情報量で、空間の『文脈』を丸ごとキャプチャ。
                </p>
                {/* Stats */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between bg-[#00d4ff]/10 border border-[#00d4ff] px-4 py-3 rounded-lg">
                    <span className="text-sm text-[#00d4ff]">視野角</span>
                    <span className="text-lg font-bold text-[#00d4ff]">360°</span>
                  </div>
                  <div className="flex items-center justify-between bg-[#00d4ff]/10 border border-[#00d4ff] px-4 py-3 rounded-lg">
                    <span className="text-sm text-[#00d4ff]">情報量</span>
                    <span className="text-lg font-bold text-[#00d4ff]">12x</span>
                  </div>
                  <div className="flex items-center justify-between bg-[#4ade80]/10 border border-[#4ade80] px-4 py-3 rounded-lg">
                    <span className="text-sm text-[#4ade80]">死角</span>
                    <span className="text-lg font-bold text-[#4ade80]">0%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Asset Value Infographics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
          {/* Card 1: Defensible Moat */}
          <div className="bg-gradient-to-br from-[#1a1d23] to-[#0a0c0f] p-8 rounded-2xl border border-[#00d4ff]/30 hover:border-[#00d4ff] transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,212,255,0.3)]">
            <div className="w-16 h-16 bg-[#00d4ff]/20 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-shield-check-line text-3xl text-[#00d4ff]"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              模倣不可能な資産
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              360度データは、従来カメラでは再現不可能。一度蓄積すれば、他社が追いつけない<span className="text-[#00d4ff] font-bold">「データモート」</span>を構築できます。
            </p>
          </div>

          {/* Card 2: Contextual Intelligence */}
          <div className="bg-gradient-to-br from-[#1a1d23] to-[#0a0c0f] p-8 rounded-2xl border border-[#ff6b35]/30 hover:border-[#ff6b35] transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,107,53,0.3)]">
            <div className="w-16 h-16 bg-[#ff6b35]/20 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-brain-line text-3xl text-[#ff6b35]"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              文脈を理解するAI
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              360度データは「前後左右の状況」を含むため、AIが<span className="text-[#ff6b35] font-bold">「なぜその行動をしたか」</span>まで学習可能。単なる動作認識を超えた知能を実現します。
            </p>
          </div>

          {/* Card 3: Exponential Growth */}
          <div className="bg-gradient-to-br from-[#1a1d23] to-[#0a0c0f] p-8 rounded-2xl border border-[#4ade80]/30 hover:border-[#4ade80] transition-all duration-300 hover:shadow-[0_0_40px_rgba(74,222,128,0.3)]">
            <div className="w-16 h-16 bg-[#4ade80]/20 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-line-chart-line text-3xl text-[#4ade80]"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              指数関数的な成長
            </h3>
            <p className="text-base text-gray-400 leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              データが蓄積されるほど、AIの精度が向上。競合との差は<span className="text-[#4ade80] font-bold">時間とともに拡大</span>し、追いつけない優位性を確立します。
            </p>
          </div>
        </div>

        {/* Bottom CTA Message */}
        <div className="mt-20 text-center">
          <div className="inline-block bg-gradient-to-r from-[#00d4ff]/10 to-[#ff6b35]/10 border border-[#00d4ff] rounded-2xl p-8 max-w-4xl">
            <p className="text-2xl font-bold text-white leading-relaxed" style={{ fontFamily: 'Noto Sans JP, sans-serif' }}>
              現場の「全て」が、最強のAI教師データになる。<br />
              <span className="text-[#00d4ff]">360度ビッグデータ</span>で、他社が真似できない競争優位を構築しませんか？
            </p>
          </div>
        </div>
      </div>

      {/* Link to VR Impact */}
      <div className="mt-20 text-center">
        <a
          href="https://jollygood.co.jp/vrimpact/data-asset-value"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/50 text-cyan-400 rounded-lg hover:from-cyan-500/30 hover:to-blue-500/30 hover:border-cyan-400 transition-all duration-300 cursor-pointer whitespace-nowrap group"
        >
          <span className="font-bold">360度ビッグデータ資産価値の詳細を見る</span>
          <i className="ri-external-link-line text-xl group-hover:translate-x-1 transition-transform"></i>
        </a>
        <p className="text-gray-500 text-sm mt-3">※ 別タブで開きます（VR Impactページへ）</p>
      </div>

      <style>{`
        @keyframes scanHorizontal {
          0%, 100% {
            transform: translateX(-10px);
            opacity: 0.3;
          }
          50% {
            transform: translateX(10px);
            opacity: 0.8;
          }
        }
        
        @keyframes scanVertical {
          0%, 100% {
            transform: translateY(-10px);
            opacity: 0.3;
          }
          50% {
            transform: translateY(10px);
            opacity: 0.7;
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </section>
  );
}
